import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LoginPage = () => {
  const { user, login, detectedSchoolCode } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [schoolBranding, setSchoolBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(!!detectedSchoolCode);

  // Fetch school branding from subdomain
  useEffect(() => {
    if (detectedSchoolCode) {
      setBrandingLoading(true);
      axios.get(`/schools/lookup/${detectedSchoolCode}`)
        .then(res => {
          setSchoolBranding(res.data);
          setBrandingLoading(false);
        })
        .catch(() => {
          setBrandingLoading(false);
        });
    }
  }, [detectedSchoolCode]);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.username, formData.password, totpCode);

    if (result.success) {
      toast.success('Inicio de sesión exitoso');
    } else {
      setError(result.error);
      if (result.needsTotp) setNeedsTotp(true);
      toast.error('Error al iniciar sesión');
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isSchoolLogin = !!detectedSchoolCode;
  const accentColor = schoolBranding?.accent_color || '#f97316';
  const primaryColor = schoolBranding?.primary_color || '#1e40af';

  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#f97316' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200"></div>
      
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-blue-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-indigo-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-slate-300 rounded-full opacity-15 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md flex-1 flex flex-col justify-center">
        <div className="text-center mb-8">
          {isSchoolLogin && schoolBranding?.logo_url ? (
            <img src={schoolBranding.logo_url.startsWith('data:') ? schoolBranding.logo_url : `${BACKEND_URL}${schoolBranding.logo_url}`} alt={schoolBranding.name}
              className="w-24 h-24 mx-auto mb-4 object-contain drop-shadow-lg rounded-2xl" />
          ) : isSchoolLogin && schoolBranding ? (
            <div className="w-24 h-24 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white text-3xl font-bold drop-shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}>
              {schoolBranding.name.charAt(0)}
            </div>
          ) : (
            <img src="/logo-jk.png" alt="EduCore" className="w-24 h-24 mx-auto mb-4 object-contain drop-shadow-lg" />
          )}
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {isSchoolLogin && schoolBranding ? (
              schoolBranding.name
            ) : (
              <span>
                EduCore
                <span className="relative inline-block">
                  <span>®</span>
                  <span className="absolute left-full bottom-1/2 text-[9px] font-bold tracking-[0.15em] text-slate-500 leading-none ml-0.5" data-testid="brand-tag">BRAND</span>
                </span>
              </span>
            )}
          </h1>
          <p className="text-slate-600">
            {isSchoolLogin ? 'Portal Escolar' : 'Panel de Administración'}
          </p>
        </div>

        <Card className="glass-effect border-0 shadow-2xl">
          <CardHeader className="space-y-2 pb-6">
            <CardTitle className="text-2xl font-bold text-center text-slate-800">
              Iniciar Sesión
            </CardTitle>
            <CardDescription className="text-center text-slate-600">
              {isSchoolLogin ? 'Ingresa tus credenciales de acceso' : 'Acceso Super Administrador'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-slate-700">Usuario</Label>
                <Input id="username" name="username" type="text" placeholder="Ingresa tu usuario"
                  value={formData.username} onChange={handleChange} required disabled={loading}
                  className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  data-testid="username-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Contraseña</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="password" name="password" type="password" placeholder="Ingresa tu contraseña"
                    value={formData.password} onChange={handleChange} required disabled={loading}
                    className="h-12 pl-10 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="password-input" />
                </div>
              </div>

              {needsTotp && <div className="space-y-2"><Label htmlFor="totp_code">Código de verificación o recuperación</Label><Input id="totp_code" autoComplete="one-time-code" value={totpCode} onChange={e=>setTotpCode(e.target.value)} required maxLength={64}/></div>}
              <Button type="submit" disabled={loading || !formData.username || !formData.password}
                className="w-full h-12 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                style={{ background: `linear-gradient(135deg, ${accentColor}, ${primaryColor})` }}
                data-testid="login-button">
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="spinner mr-2"></div>
                    Iniciando sesión...
                  </div>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>
            <a href="/reset-password" className="block text-center text-sm text-blue-700 mt-4">Recuperar acceso</a>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500">
            {isSchoolLogin
              ? '¿Problemas para acceder? Contacta al administrador de tu escuela'
              : '¿Problemas para acceder? Contacta al administrador del sistema'}
          </p>
        </div>
      </div>

      {/* Footer with JK branding */}
      <div className="relative w-full max-w-md mt-8 mb-4">
        <div className="flex flex-col items-center gap-2">
          {isSchoolLogin && (
            <div className="flex items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <span className="text-xs text-slate-400">Powered by</span>
              <img src="/logo-jk.png" alt="JK Enterprise" className="w-6 h-6 object-contain" />
              <span className="text-xs font-semibold text-slate-500">
                EduCore
                <span className="relative inline-block">
                  <span>®</span>
                  <span className="absolute left-full bottom-1/2 text-[6px] font-bold tracking-[0.1em] text-slate-500 leading-none ml-0.5">BRAND</span>
                </span>
              </span>
            </div>
          )}
          <p className="text-[11px] text-slate-400">
            &copy; {new Date().getFullYear()} JK Enterprise. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
